import React, { useEffect, useRef, useState } from 'react';
import { motion, Variants } from 'framer-motion';
import { BiRocket, BiSolidUserVoice, BiBadgeCheck } from 'react-icons/bi';

interface Feature {
  iconName: string;
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    iconName: "rocket",
    title: "Production-First Engineering",
    description: "Your business can't afford AI experiments that never reach production. We start every project with evaluation frameworks, cost controls, and rollback strategies built-in. While others deliver prototypes, we deliver systems that scale reliably from day one."
  },
  {
    iconName: "check",
    title: "AI That Makes Business Sense",
    description: "We choose technology based on your constraints: data availability, latency requirements, interpretability needs, and budget realities. Our unified evaluation framework measures quality, reliability, safety, and ROI across all AI approaches."
  },
  {
    iconName: "voice",
    title: "Partnership That Transfers Power",
    description: "We work beside your engineers and product leaders, not above them. Every engagement includes hands-on training, comprehensive documentation, and knowledge transfer. You get working systems and the capability to extend them."
  }
];

const getIcon = (iconName: string) => {
  switch (iconName) {
    case 'rocket':
      return <BiRocket className="w-8 h-8 text-custom-indigo-500 group-hover:text-white transition-colors duration-300" />;
    case 'check':
      return <BiBadgeCheck className="w-8 h-8 text-custom-indigo-500 group-hover:text-white transition-colors duration-300" />;
    case 'voice':
      return <BiSolidUserVoice className="w-8 h-8 text-custom-indigo-500 group-hover:text-white transition-colors duration-300" />;
    default:
      return <BiRocket className="w-8 h-8 text-custom-indigo-500 group-hover:text-white transition-colors duration-300" />;
  }
};

const WhoWeAreSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants: Variants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section ref={sectionRef} className="relative border-b" id="who-we-are" style={{borderColor: '#D8CEBD'}}>
      <div className="container py-16 lg:py-24 mx-auto px-4">
        <motion.div
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          variants={containerVariants}
          className="flex flex-wrap -mx-4 mb-12 lg:mb-16 justify-center items-end"
        >
          <div className="w-full px-4 mx-auto text-left">
            <motion.div variants={itemVariants} className="mb-6">
              <span className="text-gray-500 dark:text-gray-400 font-medium text-lg">Welcome to Flux Labs</span>
            </motion.div>
            <motion.div variants={itemVariants} className="mb-6">
              <h2 className="text-4xl lg:text-5xl leading-tight font-medium mb-6">
              We build you production-ready systems while your competition builds demos
              </h2>
            </motion.div>
            <motion.div variants={itemVariants} className="mb-6 text-left">
              <span className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed mb-8">
                We help you ship AI‑powered software you can trust. Flux Labs designs and builds applications, data and AI
                systems, and the internal platforms that keep them fast and safe. 
              </span>
            </motion.div>
            <motion.div variants={itemVariants} className="mb-6 text-left">
              <span className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed mb-8">
                We integrate modern LLMs and agentic patterns when they add real value, and we deliver traditional machine learning for forecasting, scoring, and
                optimization when that’s the right answer. 
              </span>
            </motion.div>         
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          variants={containerVariants}
          className="flex flex-wrap -mx-4 justify-around"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="w-full lg:w-1/3 px-4 mb-8 lg:mb-0"
            >
              <div className="text-center group">
                <div className="mb-6 flex justify-center">
                  <div className="w-16 h-16 bg-custom-indigo-100 dark:bg-custom-indigo-900/30 rounded-full flex items-center justify-center group-hover:bg-custom-indigo-500 transition-colors duration-300">
                    {getIcon(feature.iconName)}
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold mb-4 group-hover:text-custom-indigo-500 transition-colors duration-300">
                  {feature.title}
                </h3>
                
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default WhoWeAreSection;